import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { getDisplayRouteLanguageFromPathname, normalizeDisplayRouteLanguage } from '../utils/displayRouteLanguage';

const LanguageContext = createContext(undefined);

const ENABLED_LANGUAGES_KEY = 'enabled_languages';
const SUPPORTED_LANGUAGES = ['en', 'fr', 'es'];

function detectBrowserLanguage() {
  if (typeof navigator === 'undefined') return null;
  const candidates = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language || navigator.userLanguage];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const base = candidate.toLowerCase().split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(base)) return base;
  }
  return null;
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const routeLanguage = typeof window !== 'undefined'
      ? getDisplayRouteLanguageFromPathname(window.location.pathname)
      : null;
    if (routeLanguage) return routeLanguage;

    try {
      const saved = localStorage.getItem('language');
      if (saved) return saved;
    } catch (error) {
      console.error('Error reading language from localStorage:', error);
    }

    // No explicit choice saved — auto-detect from the browser's preferred language.
    return detectBrowserLanguage() || 'en';
  });
  const [enabledLanguages, setEnabledLanguages] = useState(() => {
    try {
      const saved = localStorage.getItem(ENABLED_LANGUAGES_KEY);
      if (saved) return JSON.parse(saved);
      const siteDetails = localStorage.getItem('site_details');
      if (siteDetails) {
        const parsed = JSON.parse(siteDetails);
        if (parsed.supportedLanguages) {
          const langMap = { English: 'en', French: 'fr', Spanish: 'es', German: 'de', Italian: 'it', Portuguese: 'pt' };
          return parsed.supportedLanguages.split(',').map(l => langMap[l.trim()] || l.trim().toLowerCase().slice(0, 2));
        }
      }
    } catch (e) {}
    return ['en'];
  });

  useEffect(() => {
    try {
      localStorage.setItem('language', language);
      document.documentElement.lang = language;
    } catch (error) {
      console.error('Error saving language to localStorage:', error);
    }
  }, [language]);

  const toggleLanguage = useCallback((lang) => {
    const normalized = normalizeDisplayRouteLanguage(lang) || lang;
    if (enabledLanguages.includes(normalized)) {
      setLanguage(normalized);
    }
  }, [enabledLanguages]);

  const setRouteLanguage = useCallback((lang) => {
    const normalized = normalizeDisplayRouteLanguage(lang);
    if (normalized) {
      setLanguage(normalized);
    }
  }, []);

  const updateEnabledLanguages = (languages) => {
    setEnabledLanguages(languages);
    localStorage.setItem(ENABLED_LANGUAGES_KEY, JSON.stringify(languages));
  };

  const contextValue = { language, toggleLanguage, setRouteLanguage, enabledLanguages, updateEnabledLanguages };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
